import React from 'react';
import { X, Zap, CalendarDays, UserCheck, Users, Target, Receipt, Crown, Gift, HelpCircle, Link as LinkIcon, ShoppingBag } from 'lucide-react';

interface HowToEarnPointsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const HowToEarnPointsModal: React.FC<HowToEarnPointsModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    const earningWays = [
        {
            icon: <UserCheck className="w-5 h-5 text-teal-500" />,
            title: 'Cadastro Completo',
            description: 'Preencha as informações do seu perfil para ganhar seus primeiros pontos.',
            bg: 'bg-teal-100 dark:bg-teal-900/30'
        },
        {
            icon: <CalendarDays className="w-5 h-5 text-blue-500" />,
            title: 'Login Diário',
            description: 'Entre todos os dias para ganhar pontos. Manter uma sequência te dá bônus extras!',
            bg: 'bg-blue-100 dark:bg-blue-900/30'
        },
        {
            icon: <LinkIcon className="w-5 h-5 text-purple-500" />,
            title: 'Primeiras Ações (Conquistas)',
            description: 'Você recebe pontos extras ao otimizar seu 1º link, fazer seu 1º resgate, ou ter a 1ª compra aprovada.',
            bg: 'bg-purple-100 dark:bg-purple-900/30'
        },
        {
            icon: <Receipt className="w-5 h-5 text-emerald-500" />,
            title: 'Compras em Parceiros',
            description: 'Compre usando os links otimizados e solicite seus pontos anexando o comprovante.',
            bg: 'bg-emerald-100 dark:bg-emerald-900/30'
        },
        {
            icon: <Users className="w-5 h-5 text-indigo-500" />,
            title: 'Indique e Ganhe',
            description: 'Convide amigos para a plataforma. Quando eles fizerem a primeira ação validada, você ganha pontos!',
            bg: 'bg-indigo-100 dark:bg-indigo-900/30'
        },
        {
            icon: <Target className="w-5 h-5 text-fuchsia-500" />,
            title: 'Missões Semanais',
            description: 'Cumpra missões como otimizar links toda semana para garantir pontos bônus.',
            bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/30'
        },
        {
            icon: <Crown className="w-5 h-5 text-yellow-500" />,
            title: 'Bônus Mensal de Nível',
            description: 'Ao atingir níveis mais altos (Prata, Ouro, Diamante), você ganha um bônus de pontos mensal.',
            bg: 'bg-yellow-100 dark:bg-yellow-900/30'
        },
        {
            icon: <Gift className="w-5 h-5 text-pink-500" />,
            title: 'Bônus de Aniversário',
            description: 'No mês do seu aniversário, você ganha um bônus especial de presente do Radar!',
            bg: 'bg-pink-100 dark:bg-pink-900/30'
        }
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400">
                            <Zap className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Como Ganhar Pontos</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 font-medium text-balance">
                        Existem várias formas de acumular pontos no Radar da Economia. Confira abaixo as principais maneiras de turbinar seu saldo:
                    </p>

                    <div className="grid grid-cols-1 gap-4">
                        {earningWays.map((way, index) => (
                            <div key={index} className="flex gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-900/50 transition-colors">
                                <div className={`flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full ${way.bg}`}>
                                    {way.icon}
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                                        {way.title}
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                                        {way.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl flex items-center gap-3">
                        <HelpCircle className="w-8 h-8 text-blue-500 flex-shrink-0" />
                        <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                            Fique de olho nas <strong className="text-gray-900 dark:text-white">Missões</strong> e nos seus multiplicadores de <strong className="text-gray-900 dark:text-white">Nível</strong>! Eles podem aumentar bastante os pontos que você já ganha normalmente.
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
};
